import type { AgentId, AgentModeKey, LlmRoutePurposeAny } from '@developerz/domain';
import { ORG_ONLY_ROUTE_PURPOSES } from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ModeSaves } from '../lib/agent-mode-save';
import type { ModelSaves } from '../lib/agent-model-save';
import { type AgentModeView, withTool } from '../lib/agent-modes';
import {
  CODER_TASK_TAG_PURPOSES,
  type CoderTaskTagPurpose,
  routePinTriggerValue,
} from '../lib/agents';
import type { KeyRow } from '../lib/keys-types';
import type { RouteWire } from '../lib/llmRoutes';
import type { RepoRouteSaves } from '../lib/repo-route-save';
import { ActionDrawer } from './ActionDrawer';
import { AgentModeToolsField } from './AgentModeToolsField';
import { AgentRepoOverridesSection } from './AgentRepoOverridesSection';
import { AgentRouteAdvancedFields } from './AgentRouteAdvancedFields';
import { ModelPicker } from './ModelPicker';
import { ModelSaveNotice } from './ModelSaveNotice';
import { RouteRefusalNotice } from './RouteRefusalNotice';

// The Agents page's Advanced drawer (#5211, epic #5200) — `ActionDrawer` opened
// from `AgentCard`. Every ROUTE edit here (thinking depth, specific key, one
// provider, a task-tag row, a repo pin) is `nextRouteRow`'s full intended row,
// sent through `ModelSaves.choose` (org scope) or `RepoRouteSaves.choose` (repo
// scope) — never a hand-built partial PUT. Tools narrow one `agent_modes` row
// through `ModeSaves`; the mode SWITCH itself lives on the card
// (`AgentModeSwitch`), not here.

/** The three task-tag purposes' labels — reuses `ChangedByChip`'s own key spelling
 * (`agents.changed_by.purpose.*`), not a second vocabulary for the same purposes. */
const TASK_TAG_LABEL_KEY: Readonly<Record<'coding' | 'quick' | 'debugging-qa', string>> = {
  coding: 'agents.changed_by.purpose.coding',
  quick: 'agents.changed_by.purpose.quick',
  'debugging-qa': 'agents.changed_by.purpose.debugging-qa',
};

export type AgentAdvancedDrawerProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly agent: AgentId;
  readonly agentName: string;
  readonly purpose: LlmRoutePurposeAny;
  readonly route: RouteWire | undefined;
  readonly keys: { readonly data: readonly KeyRow[] | undefined; readonly isError: boolean };
  readonly canEdit: boolean;
  readonly saves: ModelSaves;
  /** Re-read after a key rename — it may have moved this purpose's own pin. */
  readonly onKeyRenamed: () => void;

  readonly modeKey: AgentModeKey | undefined;
  readonly modeView: AgentModeView | undefined;
  readonly canEditModes: boolean;
  readonly modeSaves: ModeSaves;

  readonly repoRoutes: readonly RouteWire[];
  readonly repoNames: ReadonlyMap<string, string>;
  readonly repoSaves: RepoRouteSaves;
  readonly token: string | null;

  /** Coder only: the org routes for `CODER_TASK_TAG_PURPOSES`, purpose → row. */
  readonly taskTagRoutes: ReadonlyMap<string, RouteWire>;
  /** Coder only: each task-tag purpose's REPO-scope rows, for its own
   * per-repo section (#5436). Built by `repoRoutesFor`, same as
   * {@link repoRoutes} is for the card's own purpose. */
  readonly taskTagRepoRoutes: ReadonlyMap<CoderTaskTagPurpose, readonly RouteWire[]>;
};

export function AgentAdvancedDrawer(props: AgentAdvancedDrawerProps): JSX.Element {
  return (
    <ActionDrawer
      open={props.open}
      title={t('agents.drawer.title', { agent: props.agentName })}
      onClose={props.onClose}
    >
      <AgentRouteAdvancedFields
        idPrefix={`agent-drawer-${props.agent}`}
        route={props.route}
        keys={props.keys}
        canEdit={props.canEdit}
        busy={props.saves.isBusy(props.purpose)}
        onChange={(change) => void props.saves.choose(props.purpose, change)}
        token={props.token}
        onKeyRenamed={props.onKeyRenamed}
      />
      <ModelSaveNotice
        pending={props.saves.refusal(props.purpose)}
        busy={props.saves.isBusy(props.purpose)}
        onApplyAnyway={() => void props.saves.applyAnyway(props.purpose)}
        onApplyReset={() => void props.saves.applyWithoutEffort(props.purpose)}
        onDismiss={() => props.saves.dismiss(props.purpose)}
      />

      <Show when={props.modeView}>
        {(view) => (
          <>
            <AgentModeToolsField
              view={view()}
              idPrefix={`agent-drawer-${props.agent}`}
              busy={props.modeSaves.isBusy(view().key)}
              canEdit={props.canEditModes}
              onTool={(tool, on) =>
                void props.modeSaves.setTools(view(), withTool(view(), tool, on))
              }
            />
            <RouteRefusalNotice
              refusal={props.modeSaves.refusal(view().key)}
              onDismiss={() => props.modeSaves.dismiss(view().key)}
            />
          </>
        )}
      </Show>
      <Show when={props.modeKey !== undefined && !props.canEditModes}>
        <p class="field__desc">{t('agents.drawer.tools_locked')}</p>
      </Show>

      {/* An org-only purpose (#6388, `ORG_ONLY_ROUTE_PURPOSES`) has no repo rows: its
          reader reads the org row, and the write seam refuses a repo pin. */}
      <Show when={!ORG_ONLY_ROUTE_PURPOSES.includes(props.purpose)}>
        <AgentRepoOverridesSection
          agentName={props.agentName}
          purpose={props.purpose}
          repoRoutes={props.repoRoutes}
          repoNames={props.repoNames}
          keys={props.keys}
          canEdit={props.canEdit}
          saves={props.repoSaves}
          modeKey={props.modeKey}
          canEditModes={props.canEditModes}
          token={props.token}
          title={t('agents.drawer.repo.title')}
          ariaLabel={t('agents.drawer.repo.aria')}
          onKeyRenamed={props.onKeyRenamed}
        />
      </Show>

      <Show when={props.agent === 'coder'}>
        <section class="agent-drawer__section" aria-label={t('agents.drawer.task_tags.aria')}>
          <h3 class="agent-drawer__section-title">{t('agents.drawer.task_tags.title')}</h3>
          <For each={CODER_TASK_TAG_PURPOSES}>
            {(purpose) => {
              const route = () => props.taskTagRoutes.get(purpose);
              const purposeLabel = () => t(TASK_TAG_LABEL_KEY[purpose]);
              return (
                <div class="agent-drawer__task-tag">
                  <p class="agent-drawer__task-tag-label">{purposeLabel()}</p>
                  <ModelPicker
                    agentName={purposeLabel()}
                    purpose={purpose}
                    route={route()}
                    pinValue={routePinTriggerValue(route())}
                    automaticLabel={t('agents.drawer.task_tags.unpinned')}
                    keys={props.keys}
                    canEdit={
                      props.canEdit && props.saves.refusal(purpose)?.refusal.kind !== 'locked'
                    }
                    busy={props.saves.isBusy(purpose)}
                    onChoose={(change) => void props.saves.choose(purpose, change)}
                  />
                  <AgentRouteAdvancedFields
                    idPrefix={`agent-drawer-task-tag-${purpose}`}
                    route={route()}
                    keys={props.keys}
                    canEdit={
                      props.canEdit && props.saves.refusal(purpose)?.refusal.kind !== 'locked'
                    }
                    busy={props.saves.isBusy(purpose)}
                    onChange={(change) => void props.saves.choose(purpose, change)}
                    token={props.token}
                    onKeyRenamed={props.onKeyRenamed}
                  />
                  <ModelSaveNotice
                    pending={props.saves.refusal(purpose)}
                    busy={props.saves.isBusy(purpose)}
                    onApplyAnyway={() => void props.saves.applyAnyway(purpose)}
                    onApplyReset={() => void props.saves.applyWithoutEffort(purpose)}
                    onDismiss={() => props.saves.dismiss(purpose)}
                  />
                  <AgentRepoOverridesSection
                    agentName={purposeLabel()}
                    purpose={purpose}
                    repoRoutes={props.taskTagRepoRoutes.get(purpose) ?? []}
                    repoNames={props.repoNames}
                    keys={props.keys}
                    canEdit={props.canEdit}
                    saves={props.repoSaves}
                    modeKey={undefined}
                    canEditModes={props.canEditModes}
                    token={props.token}
                    title={t('agents.drawer.repo.task_tag_title', { purpose: purposeLabel() })}
                    ariaLabel={t('agents.drawer.repo.task_tag_aria', { purpose: purposeLabel() })}
                    onKeyRenamed={props.onKeyRenamed}
                  />
                </div>
              );
            }}
          </For>
        </section>
      </Show>
    </ActionDrawer>
  );
}
